<template>
  <HelpMessage>
    <strong>Schedule</strong> is where you organize the consumptions.
    <ol>
      <li>Adds <strong>Days</strong> to Event (hover the event name to display actions)</li>
      <li>Adds <strong>Rows</strong></li>
      <li v-if="session.is_template">Drag a <strong>day column</strong> header to reorder days</li>
    </ol>
  </HelpMessage>

  <div v-if="!isMounted" :style="{ height: 'calc(100vh - 11rem)' }">
    <Spinner />
  </div>

  <DataTable :value="session.rows" dataKey="id" showGridlines v-if="session.events.length > 0 && isMounted"
    :scrollable="true" scrollHeight="calc(100vh - 11rem)" :class="{ 'hide-dates': !displayDates }"
    @rowReorder="session.rows = $event.value" editMode="cell" class="editable-cells-table session-table schedule-table"
    @cell-edit-complete="onCellEditComplete" :rowClass="rowClass">
    <ColumnGroup type="header">
      <Row>
        <!-- Top Left Cell -->
        <Column class="top-left-cell transparent hide-print" frozen :rowspan="displayDates ? 3 : 2" :colspan="2">
          <template #header>
            <PrintButton />
            <div class="d-flex flex-column ms-4">
              <NewRowButton @add-row="addRow" />
              <Button type="button" icon="pi pi-plus" label="Event" class="mt-2 p-button-sm p-button-outlined"
                @click="$refs.eventForm.show()" v-if="!session.is_template" />

            </div>
          </template>
        </Column>
        <!-- Event Header -->
        <Column v-for="(event, index) in session.events" :colspan="event.days.length" :key="event.id"
          class="event-start event-end header-group event-editor" :class="`event-${event.id}`">
          <template #header>
            <div class="d-flex align-items-center w-100">
              <span class="flex-grow-1 text-center">
                {{ event.name }}
                <span
                  v-if="event.people_count || (event.people_count_by_day && Object.keys(event.people_count_by_day).length)"
                  class="fw-normal ms-2 xs d-inline-flex align-items-center">
                  <template v-if="event.people_count_by_day && event.days && event.days.length">
                    {{event.days.map((_, i) => getPeopleCountForDay(event, i)).join(' / ')}}
                  </template>
                  <template v-else>{{ event.people_count }}</template>
                  <span class="pi pi-users xs ms-1"></span>
                </span>
              </span>
              <span class="d-print-none btn-on-hover">
                <Button icon="pi pi-save" @click="$refs.saveTemplate.show(event)"
                  class="p-button-sm p-button-success p-button-text" v-tooltip.top="'Save as template'"
                  v-if="!session.is_template" />
                <Button icon="pi pi-pencil" @click="$refs.eventForm.show(event, session.is_template)"
                  class="p-button-sm p-button-text" />
                <Button icon="pi pi-trash" @click="session.events.splice(index, 1)"
                  class="p-button-sm p-button-danger p-button-text" v-if="!session.is_template" />
                <Button icon="pi pi-plus" label="Day" class="p-button-sm p-button-secondary btn-add-day"
                  @click="addDayToEvent(event)" :disabled="disableAddDayFor(event)" />
              </span>
            </div>
          </template>
        </Column>
      </Row>
      <Row>
        <!-- Day Date Header -->
        <Column v-for="day in sessionDays" :key="`header-date-${day.id}`" :class="[day.class, 'day-date']">
          <template #header>
            <span>{{ day.dateHeader }}</span>
          </template>
        </Column>
      </Row>
      <Row>
        <!-- Day Name Header -->
        <Column v-for="day in sessionDays" :key="`header-${day.id}`" :class="day.class" class="day-label">
          <template #header>
            <div class="day-header" :draggable="isTemplate" :class="dayHeaderClass(day)"
              @dragstart="isTemplate && onDayDragStart($event, day)" @dragend="onDayDragEnd"
              @dragover.prevent="isTemplate && onDayDragOver($event, day)"
              @dragleave="onDayDragLeave($event, day)" @drop.prevent="isTemplate && onDayDrop($event, day)">
              <Button v-if="isTemplate" type="button" tabindex="-1" aria-label="Drag to reorder days"
                class="day-drag-grip p-button-text p-button-sm" v-tooltip.top="'Drag to reorder days'">
                <span class="pi pi-bars" />
              </Button>
              <Button icon="pi pi-plus" class="btn-on-hover btn-on-hover-left p-button-text p-0"
                v-tooltip.top="'Add a day before'" @click="insertDayInEvent(day.event, day.index)" />
              <InputText :value="day.label" @change="day.event.days[day.index] = $event.target.value"
                class="day-input" />
              <Button icon="pi pi-trash" class="btn-on-hover p-button-danger p-button-text p-0"
                v-if="day.event.days.length > 1" @click="confirmRemoveDay(day)" />
            </div>
          </template>
        </Column>
      </Row>
    </ColumnGroup>

    <Column :rowReorder="true" class="reorder-column d-print-none" frozen />

    <!-- First Column : Row Type -->
    <Column frozen class="product-column">
      <template #body="{ data }">
        <span v-if="data.type == 'product'">
          {{ $root.getProduct(data.product_id).name }}
          <span v-if="$root.getProduct(data.product_id).unit">({{ $root.getProduct(data.product_id).unit }})</span>
        </span>
        <span v-else-if="data.type == 'recipie'">{{ $root.getRecipie(data.recipie_id).name }}</span>
        <span v-else class="variable-row-label">{{ data.label }}</span>
        <span class="btn-on-hover d-print-none">
          <ToggleButton v-model="data.printable" onIcon="pi pi-print" offIcon="pi pi-print slash" @click.stop
            title="Printable ?" class="p-button-sm" />
          <Button icon="pi pi-trash" @click.prevent="deleteRow(data)"
            class=" p-button-small p-button-danger p-button-text" />
        </span>
      </template>
      <template #editor="{ data }">
        <InputProduct v-if="data.type == 'product'" v-model="data.product_id" class="editor-sm w-100" />
        <InputRecipie v-else-if="data.type == 'recipie'" v-model="data.recipie_id" />
        <InputText v-else v-model="data.label" placeholder="Row Name" />
      </template>
    </Column>

    <!-- Cells -->
    <Column v-for="day in sessionDays" :key="`cell-${day.id}`" :field="day.id" :class="day.class">
      <!-- Cell Show -->
      <template #body="{ data, field }">
        <template v-if="data.values[field]">
          <label v-if="data.type == 'products'">
            {{ $root.getProduct(data.values[field].product_id).name }}
            <span v-if="$root.getProduct(data.values[field].product_id).unit">({{
              $root.getProduct(data.values[field].product_id).unit }})</span>
          </label>
          <label v-if="data.type == 'recipies' && data.values[field].recipie_id">
            {{ $root.getRecipie(data.values[field].recipie_id).name }}
            <span class="pi pi-clock d-print-none" v-tooltip="'Needed to be prepared the day before'"
              style="font-size: 0.7rem"
              v-if="$root.getRecipie(data.values[field].recipie_id).prepare_day_before"></span>
          </label>
          <div class="amount"
            v-if="data.type == 'recipies' && data.values[field].recipie_id || data.type == 'products' && data.values[field].product_id || ['recipie', 'product'].includes(data.type)">
            {{ data.values[field].amount }}
          </div>
        </template>
      </template>
      <!-- Cell Edit -->
      <template #editor="{ data, field }">
        <div :class="inCellEditorClass(data)">
          <InputProduct v-if="data.type == 'products'" v-model="data.values[field].product_id" class="w-100" />
          <InputRecipie v-else-if="data.type == 'recipies'" v-model="data.values[field].recipie_id" />
          <div class="p-inputgroup">
            <InputNumber v-model="data.values[field].amount" placeholder="Amount" autofocus :maxFractionDigits="2" />
            <span class="p-inputgroup-addon rounded-0 p-0" v-show="data.values[field].product_id">
              {{ $root.getProduct(data.values[field].product_id).unit }}
            </span>
            <Button icon="pi pi-sync" v-show="data.values[field].recipie_id" class="p-button-secondary"
              @click="updateRecipieAmounts(data.values[field], getPeopleCountForDay(day.event, day.index))"
              v-tooltip="'Update recipie to fit with event number of people'" />
          </div>
        </div>
      </template>
    </Column>
  </DataTable>

  <EventForm ref="eventForm" @save="createOrUpdateEvent($event)" :disabled-dates="sessionDays.map(d => d.date)"
    :default-date="sessionDays.length > 1 ? sessionDays.at(-1).date.addDays(1) : null" />

  <SaveTemplate ref="saveTemplate" />
</template>

<script>
import ColumnGroup from 'primevue/columngroup'
import Row from 'primevue/row'
import InputNumber from 'primevue/inputnumber'
import ToggleButton from 'primevue/togglebutton'
import InputProduct from '@/components/InputProduct.vue'
import InputRecipie from '@/components/InputRecipie.vue'
import EventForm from './EventForm.vue'
import NewRowButton from './SessionScheduleNewRowButton.vue'
import PrintButton from './SessionSchedulePrintButton.vue'
import SaveTemplate from '@/views/templates/SaveTemplateDialog.vue'
import Spinner from '@/components/Spinner.vue'
import CalendarMixin from '@/services/calendar-mixin'
import { reorderDaysInEvent } from '@/services/session-days'
import ScheduleMenu from './SessionScheduleMenu.vue'

export default {
  inject: ['sessionDays', 'removeDayFromEvent', 'insertDayInEvent'],
  mixins: [CalendarMixin],
  components: {
    ColumnGroup,
    Row,
    InputProduct,
    InputRecipie,
    InputNumber,
    NewRowButton,
    EventForm,
    ToggleButton,
    PrintButton,
    SaveTemplate,
    Spinner,
    ScheduleMenu,
  },
  data() {
    return {
      draggedDay: null,
      dropTarget: null,
    }
  },
  mounted() {
    if (this.session.events.length === 0) this.$refs.eventForm.show({}, this.session.is_template)
  },
  computed: {
    session() {
      return this.$root.session
    },
    isTemplate() {
      return !!this.session.is_template
    },
    displayDates() {
      return !this.session.is_template
    },
  },
  methods: {
    getPeopleCountForDay(event, dayIndex) {
      const byDay = event.people_count_by_day
      if (byDay && byDay[dayIndex] != null) return byDay[dayIndex]
      return event.people_count
    },
    addDayToEvent(event) {
      event.days.push(`Day ${event.days.length}`)
      if (event.people_count_by_day) {
        const lastIndex = event.days.length - 1
        const prevCount = event.people_count_by_day[lastIndex - 1]
        event.people_count_by_day[lastIndex] = prevCount ?? event.people_count
      }
    },
    confirmRemoveDay(day) {
      if (this.session.is_template) {
        this.removeDayFromEvent(day.event, day.index)
        return
      }
      this.$confirm.require({
        message: `Delete "${day.label}"? Orders and inventories linked to this day will also be deleted. Later days will shift.`,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.removeDayFromEvent(day.event, day.index)
        },
      })
    },
    dayHeaderClass(day) {
      const classes = []
      if (this.isTemplate) classes.push('day-draggable')
      if (this.draggedDay && this.draggedDay.id === day.id) classes.push('day-dragging')
      if (this.dropTarget && this.dropTarget.day.id === day.id) {
        classes.push(this.dropTarget.after ? 'day-drop-after' : 'day-drop-before')
      }
      return classes
    },
    onDayDragStart(event, day) {
      // Ignore text drags started from the day label input
      if (event.target.closest('input')) {
        event.preventDefault()
        return
      }
      this.draggedDay = day
      this.dropTarget = null
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', day.id)
    },
    onDayDragEnd() {
      this.draggedDay = null
      this.dropTarget = null
    },
    onDayDragOver(event, day) {
      if (!this.draggedDay || this.draggedDay.event !== day.event) return
      event.dataTransfer.dropEffect = 'move'
      const rect = event.currentTarget.getBoundingClientRect()
      this.dropTarget = { day, after: event.clientX > rect.left + rect.width / 2 }
    },
    onDayDragLeave(event, day) {
      if (event.currentTarget.contains(event.relatedTarget)) return
      if (this.dropTarget && this.dropTarget.day.id === day.id) this.dropTarget = null
    },
    onDayDrop(event, day) {
      const source = this.draggedDay
      const dropAfter = this.dropTarget && this.dropTarget.day.id === day.id && this.dropTarget.after
      this.onDayDragEnd()
      if (!source || source.event !== day.event) return
      let toIndex = day.index + (dropAfter ? 1 : 0)
      if (toIndex > source.index) toIndex -= 1
      if (toIndex === source.index) return
      reorderDaysInEvent(this, source.event, source.index, toIndex)
    },
    disableAddDayFor(event) {
      // Allow events to overlap only for one day (in case event1 ends on the morning and event2 starts on the afternoon)
      const newDate = event.start_date.addDays(event.days.length - 1)
      return this.sessionDays.find((day) => day.event !== event && day.date.toDateString() === newDate.toDateString())
    },
    onCellEditComplete(event) {
      // When creating an object from a modal any click on a dropdown inside the modal was
      // interpreted as a click to close the cell edit, so preventing it
      if (document.querySelector('.p-dialog')) {
        event.preventDefault()
        return
      }
      const { data, newData } = event
      data.label = newData.label
      data.product_id = newData.product_id
      data.recipie_id = newData.recipie_id
    },
    rowClass(data) {
      return data.printable ? null : 'd-print-none'
    },
    inCellEditorClass(data) {
      const result = []
      if (['products', 'recipies'].includes(data.type)) {
        result.push('editor-sm')
        if (this.sessionDays.length > 5) result.push('editor-floating')
      }
      return result.join(' ')
    },
    addRow(type) {
      const row = {
        id: this.newId(this.session.rows), type, label: '', values: {}, printable: true,
      }
      this.sessionDays.forEach((day) => {
        // init amounts with event people_count (per day when day-by-day)
        const amount = ['recipie', 'recipies'].includes(type) ? this.getPeopleCountForDay(day.event, day.index) : null
        row.values[day.id] = { amount }
      })
      this.session.rows.push(row)
      this.$nextTick(() => {
        setTimeout(() => {
          const datatableDom = document.querySelector('.p-datatable-wrapper')
          datatableDom.scrollTo(0, datatableDom.scrollHeight)
          document.querySelector('.p-datatable-tbody tr:last-child td.product-column').click()
        }, 0)
      })
    },
    deleteRow(row) {
      this.session.rows = this.session.rows.filter((r) => r.id !== row.id)
    },
    createOrUpdateEvent(event) {
      const index = this.session.events.findIndex((e) => e.id === event.id)
      if (index > -1) {
        // update existing
        this.session.events[index] = event
      } else {
        // create New
        const newEventId = this.newId(this.session.events)
        if (event.templateRows) {
          this.fillSessionWithEventTemplateRows(event.templateRows, newEventId)
          delete event.templateRows
        }
        this.session.events.push({ ...event, ...{ id: newEventId } })
      }
      this.session.events.sort((a, b) => (a.start_date > b.start_date ? 1 : -1))
    },
    newId(values) {
      if (values.length === 0) return 1
      return Math.max(...values.map((r) => r.id)) + 1
    },
    fillSessionWithEventTemplateRows(templateRows, eventId = '') {
      templateRows.forEach((templateRow) => {
        const existingRow = this.session.rows
          .find((r) => ['label', 'product_id', 'recipie_id', 'type'].every((prop) => r[prop] === templateRow[prop]))
        if (existingRow) {
          Object.values(templateRow.values).forEach((rowValue, rowIndex) => {
            existingRow.values[`Event${eventId}_${rowIndex}`] = rowValue
          })
        } else {
          const values = {}
          Object.values(templateRow.values).forEach((rowValue, rowIndex) => {
            values[`Event${eventId}_${rowIndex}`] = rowValue
          })
          this.session.rows.push({ ...{ ...templateRow }, ...{ id: this.newId(this.session.rows), values } })
        }
      })
    },
    // If the event is for 100 people, and a recipie is cooked for 120 people, this method will adjust
    // recipie so that it will now be cooked for 100
    updateRecipieAmounts(dayValue, targetAmount) {
      const recipie = this.$root.getRecipie(dayValue.recipie_id)
      const ratio = (dayValue.amount / targetAmount)
      this.$confirm.require({
        message: `This will modify the recipie amounts to fit with the event people count.
                  All recipies ingredient will be multiplied by ${ratio.round(3)}
                  Are you sure to continue ?`,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
          recipie.products.forEach((p) => {
            // preserve number of decimals
            const decimals = p.amount.decimalsCount()
            p.amount = (p.amount * ratio).round(decimals + 1)
          })
          dayValue.amount = targetAmount
          this.dbUpdate('recipies', recipie)
        },
      })
    },
  },
}
</script>

<style lang="scss">
.schedule-table th.top-left-cell {
  @media screen {
    // Reorder column width + product width
    width: 249px;
    min-width: 249px !important;
    max-width: 249px !important;
  }

  @media print {
    // Product width
    width: 160px;
    min-width: 160px !important;
    max-width: 160px !important;
  }
}

.schedule-table th.event-editor {
  padding: .7rem 1rem !important;

  .p-button.p-button-sm.btn-add-day {
    padding: 0.0rem 0.5rem;
    border-radius: 4px;
    height: 2rem;
    align-self: center;
    margin-right: .5rem;
    margin-left: .25rem;

    .p-button-icon {
      font-size: .7rem;
      line-height: 1.5;
      margin-right: 5px;
    }
  }
}

.variable-row-label {
  min-height: 3rem;
  display: flex;
  align-items: center;
}

.schedule-table .pi.pi-print.slash {
  position: relative;
  color: var(--bluegray-300) !important;

  &:after {
    content: "";
    width: 20px;
    height: 2px;
    background-color: var(--bluegray-500);
    position: absolute;
    left: -4px;
    top: 45%;
    transform: rotate(45deg);
  }
}

@media print {
  .session-table.hide-amounts-on-print .amount {
    visibility: hidden;
  }

  .schedule-table.hide-dates-on-print .p-datatable-thead>tr:nth-of-type(2) {
    display: none;
  }
}

.schedule-table.hide-dates .p-datatable-thead>tr:nth-of-type(2) {
  display: none;
}

.day-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border-radius: 4px;

  &.day-draggable {
    cursor: grab;
    padding-top: 1rem;
    user-select: none;

    &:active {
      cursor: grabbing;
    }
  }

  .day-drag-grip.p-button {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 1rem;
    padding: 0;
    // Like the other cell action buttons, only reveal the grip on cell hover
    display: none;
    align-items: center;
    justify-content: center;
    color: var(--bluegray-500);
    line-height: 1;
    cursor: grab;
    border-radius: 4px 4px 0 0;

    .pi {
      font-size: .75rem;
    }

    &:hover {
      color: var(--primary-color);
      background-color: var(--bluegray-100);
    }
  }

  th:hover & .day-drag-grip.p-button {
    display: flex;
  }

  &.day-dragging {
    opacity: 0.4;
  }

  &.day-drop-before::before,
  &.day-drop-after::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 3px;
    border-radius: 2px;
    background-color: var(--primary-color);
    z-index: 2;
  }

  &.day-drop-before::before {
    left: 0;
  }

  &.day-drop-after::after {
    right: 0;
  }
}

td,
th {
  .btn-on-hover {
    display: none;
  }

  &:hover .btn-on-hover {
    display: flex;
    position: absolute;
    right: 0;

    &.btn-on-hover-left {
      right: auto;
      left: 0;
    }
  }

  &:not(:hover) .d-hover {
    display: none;
  }
}

th .btn-on-hover {
  background-color: var(--bluegray-50);
}
</style>
